import { CompanionSetupPrompt } from './CompanionSetupPrompt';
import { LanguageSwitcher } from '@/i18n/LanguageSwitcher';
import { t, useUiLocale } from '@/i18n/locale';
import { useMemo, useRef, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMe } from '@/auth/useAuth';
import type { KidPrincipal } from '@/auth/types';
import { CompanionVoicePreferences } from './CompanionVoiceControls';
import { companionApi, type BuddyApi, type BuddyMemory } from './companionApi';
import { useCompanion } from './CompanionContext';
import { CompanionSession, companionError } from './CompanionSession';
import { CompanionHelp, CompanionRoom, MemoryShelf } from './CompanionRoom';

// eslint-disable-next-line react-refresh/only-export-components -- shared companion contract and hooks
export function companionEligible(kid: KidPrincipal | null | undefined) {
  return (
    !!kid?.family_id &&
    !kid.is_ephemeral &&
    kid.account_mode !== 'institution' &&
    kid.account_mode !== 'independent'
  );
}
export function CompanionBoundary({
  kid,
  children,
}: {
  kid: KidPrincipal | null;
  children: ReactNode;
}) {
  useUiLocale();
  if (!companionEligible(kid) || !kid) return <>{children}</>;
  return (
    <CompanionSession key={kid.sub} kidId={kid.sub}>
      {children}
      <CompanionSetupPrompt />
      <CompanionHelp />
    </CompanionSession>
  );
}
export function CompanionRoute() {
  useUiLocale();
  const me = useMe('kid');
  if (me.isPending) return <p className="p-6 text-slate2">{t('Getting your buddy ready…')}</p>;
  if (me.data?.kind !== 'kid' || !companionEligible(me.data))
    return (
      <div className="p-6">
        <h1 className="section-heading">{t('A buddy space for families')}</h1>
        <p className="lead-text mt-3">
          {' '}
          {t('This companion space is available for children on a family account.')}{' '}
        </p>
        <Link className="btn-pill-primary mt-5" to="/learn">
          {' '}
          {t('Back to Learn')}{' '}
        </Link>
      </div>
    );
  return <CompanionRoom />;
}
export function CompanionParentControls({ showLanguage = true }: { showLanguage?: boolean }) {
  useUiLocale();
  const session = useCompanion(),
    [saving, setSaving] = useState(false),
    [error, setError] = useState(''),
    [deleting, setDeleting] = useState<BuddyMemory | null>(null);
  const state = session.snapshot;
  const toggle = async () => {
    if (!state || saving) return;
    setSaving(true);
    setError('');
    try {
      session.update(
        await session.client.settings(session.kidId, state.revision, !state.memory_enabled),
      );
    } catch (caught) {
      setError(companionError(caught));
    } finally {
      setSaving(false);
    }
  };
  const restore = async (id: string) => {
    if (!state || saving) return;
    setSaving(true);
    setError('');
    try {
      session.update(await session.client.restore(session.kidId, state.revision, id));
    } catch (caught) {
      setError(companionError(caught));
    } finally {
      setSaving(false);
    }
  };
  if (session.loading) return <p className="lead-text">{t('Loading companion settings…')}</p>;
  if (session.loadError || !state)
    return (
      <div>
        <p className="lead-text">{t('Could not open these companion settings.')}</p>
        <button className="btn-pill-primary mt-4" onClick={() => void session.refresh()}>
          {' '}
          {t('Try again')}{' '}
        </button>
      </div>
    );
  return (
    <div className="mx-auto max-w-3xl">
      {showLanguage && (
        <div className="mb-3 flex justify-end">
          <LanguageSwitcher />
        </div>
      )}
      <Link className="text-sm text-slate2" to={'/portal/family/' + session.kidId + '/settings'}>
        {' '}
        {t('← Child settings')}{' '}
      </Link>
      <h1 className="section-heading mt-5">{t('Your child’s companion memories')}</h1>
      <p className="lead-text mt-3">
        {' '}
        {t(
          'Your buddy privately reviews recent conversations each week to remember interests and ongoing projects. You can correct or remove these memories here.',
        )}{' '}
      </p>
      <section className="card mt-6 p-6">
        <h2 className="text-lg font-bold">{t('Remember interests and little goals')}</h2>
        <p className="mt-2 text-sm leading-relaxed text-slate2">
          {' '}
          {t(
            'When switched off, saved facts are not used in replies and new ones cannot be added.',
          )}{' '}
        </p>
        <button
          className="btn-pill-primary mt-4"
          aria-pressed={state.memory_enabled}
          disabled={saving}
          onClick={() => void toggle()}
        >
          {state.memory_enabled ? t('Turn memory off') : t('Turn memory on')}
        </button>
      </section>
      <CompanionVoicePreferences />
      <MemoryShelf />
      {!!state.forgotten?.length && (
        <section className="card p-6">
          <h2 className="text-lg font-bold">{t('Forgotten things')}</h2>
          <p className="mt-2 text-sm text-slate2">
            {' '}
            {t('These are not used in replies. You can choose to restore one.')}{' '}
          </p>
          {state.forgotten.map((memory) => (
            <div
              key={memory.id}
              className="flex flex-col gap-3 border-b border-hairline py-4 text-sm sm:flex-row sm:items-center"
            >
              <span className="min-w-0 flex-1 leading-relaxed">{memory.text}</span>
              <div className="flex shrink-0 flex-wrap gap-2">
                <button
                  className="btn-pill-ghost"
                  onClick={() => setDeleting(memory)}
                  disabled={saving}
                >
                  {' '}
                  {t('Delete permanently')}{' '}
                </button>
                <button
                  className="btn-pill-ghost"
                  disabled={saving || !state.memory_enabled}
                  onClick={() => void restore(memory.id)}
                >
                  {' '}
                  {t('Restore')}{' '}
                </button>
              </div>
            </div>
          ))}
        </section>
      )}
      {deleting && <DeleteMemoryDialog memory={deleting} onClose={() => setDeleting(null)} />}
      {error && (
        <p role="alert" className="mt-4 text-sm text-danger-600">
          {t(error)}
        </p>
      )}
    </div>
  );
}
export function CompanionParentRoute() {
  useUiLocale();
  const { kidId } = useParams<{ kidId: string }>();
  const client = useMemo<BuddyApi>(
    () => ({
      ...companionApi,
      read: (id) => companionApi.read(id, true),
      remember: (id, revision, memoryId, memory) =>
        companionApi.remember(id, revision, memoryId, memory, true),
      forget: (id, revision, memoryId) => companionApi.forget(id, revision, memoryId, true),
    }),
    [],
  );
  if (!kidId) return null;
  return (
    <CompanionSession key={kidId} kidId={kidId} audience="parent" client={client}>
      <CompanionParentControls />
    </CompanionSession>
  );
}

function DeleteMemoryDialog({ memory, onClose }: { memory: BuddyMemory; onClose: () => void }) {
  useUiLocale();
  const session = useCompanion(),
    dialog = useRef<HTMLDialogElement>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  useEffect(() => {
    const prior = document.activeElement;
    dialog.current?.showModal();
    return () => {
      if (prior instanceof HTMLElement && prior.isConnected) prior.focus();
    };
  }, []);
  return (
    <dialog
      ref={dialog}
      className="companion-dialog"
      aria-labelledby="delete-memory-heading"
      onCancel={onClose}
    >
      <h2 id="delete-memory-heading" className="text-xl font-bold">
        {' '}
        {t('Delete this memory permanently?')}{' '}
      </h2>
      <p className="my-4 text-sm leading-relaxed">
        {' '}
        {t("It will be removed from your child's saved memories and cannot be restored.")}{' '}
      </p>
      <blockquote className="rounded-2xl bg-wash-sky p-4 text-sm">{memory.text}</blockquote>
      {error && (
        <p role="alert" className="mt-3 text-sm text-danger-600">
          {t(error)}
        </p>
      )}
      <div className="mt-6 flex justify-end gap-3">
        <button className="btn-pill-ghost" disabled={busy} onClick={onClose}>
          {' '}
          {t('Keep it')}{' '}
        </button>
        <button
          className="btn-pill-primary"
          disabled={busy}
          onClick={async () => {
            if (!session.snapshot) return;
            setBusy(true);
            try {
              session.update(
                await session.client.purge(session.kidId, session.snapshot.revision, memory.id),
              );
              onClose();
            } catch (error) {
              setError(companionError(error));
              setBusy(false);
            }
          }}
        >
          {busy ? t('Deleting...') : t('Delete permanently')}
        </button>
      </div>
    </dialog>
  );
}
